/* eslint-disable @typescript-eslint/no-explicit-any */

import type { CSSProperties } from "react";

export interface Theme {
  /** @deprecated layout is no longer supported and is controlled by VI*/
  layout?: string,
  colors?: {
    background?: string;
    base?: string;
    primary?: string;
    primary_contrast?: string;
    secondary?: string;
    secondary_contrast?: string;
    neutral?: string;
    neutral_contrast?: string;
    error?: string;
    error_contrast?: string;
    success?: string;
    success_contrast?: string;
    border?: string;
    border_choice?: string;
    border_choice_input?: string;
    light?: string;
    info?: string;
    info_contrast?: string;
  },
  font_family?: string;
  font_family_url?: string;
  components?: {
    border_radius?: string;
    border_radius_offer_box?: string;
    border_radius_choice_box?: string;
    offer_box_padding?: string;
    choice_box_padding?: string;
  }
}

export enum State {
  'AL' = 'AL',
  'AK' = 'AK',
  'AS' = 'AS',
  'AZ' = 'AZ',
  'AR' = 'AR',
  'CA' = 'CA',
  'CO' = 'CO',
  'CT' = 'CT',
  'DE' = 'DE',
  'DC' = 'DC',
  'FM' = 'FM',
  'FL' = 'FL',
  'GA' = 'GA',
  'GU' = 'GU',
  'HI' = 'HI',
  'ID' = 'ID',
  'IL' = 'IL',
  'IN' = 'IN',
  'IA' = 'IA',
  'KS' = 'KS',
  'KY' = 'KY',
  'LA' = 'LA',
  'ME' = 'ME',
  'MH' = 'MH',
  'MD' = 'MD',
  'MA' = 'MA',
  'MI' = 'MI',
  'MN' = 'MN',
  'MS' = 'MS',
  'MO' = 'MO',
  'MT' = 'MT',
  'NE' = 'NE',
  'NV' = 'NV',
  'NH' = 'NH',
  'NJ' = 'NJ',
  'NM' = 'NM',
  'NY' = 'NY',
  'NC' = 'NC',
  'ND' = 'ND',
  'MP' = 'MP',
  'OH' = 'OH',
  'OK' = 'OK',
  'OR' = 'OR',
  'PW' = 'PW',
  'PA' = 'PA',
  'PR' = 'PR',
  'RI' = 'RI',
  'SC' = 'SC',
  'SD' = 'SD',
  'TN' = 'TN',
  'TX' = 'TX',
  'UT' = 'UT',
  'VT' = 'VT',
  'VI' = 'VI',
  'VA' = 'VA',
  'WA' = 'WA',
  'WV' = 'WV',
  'WI' = 'WI',
  'WY' = 'WY',
}

export enum Province {
  'AB' = 'AB',
  'BC' = 'BC',
  'MB' = 'MB',
  'NB' = 'NB',
  'NL' = 'NL',
  'NS' = 'NS',
  'ON' = 'ON',
  'PE' = 'PE',
  'QC' = 'QC',
  'SK' = 'SK',
  'NT' = 'NT',
  'NU' = 'NU',
  'YT' = 'YT'
}

export type StateType = keyof typeof State;
export type ProvinceType = keyof typeof Province;

export enum EventType {
  'init' = 'vi.element.init',
  'update' = 'vi.element.update',
  'resize' = 'vi.element.resize',
  'validate' = 'vi.element.validate',
  'ready' = 'vi.element.ready',
  'offer_state_ready' = 'vi.element.offer_state_ready',
  'offer_state_change' = 'vi.element.offer_state_change',
  'purchase_completed' = 'vi.element.purchase_completed',
  'tokenize_payment' = 'vi.element.tokenize_payment',
  'filter_offers' = 'vi.element.filter_offers',
  'filter_offers_result' = 'vi.element.filter_offers_result',
  'event_insurance_requirements_state_change' = 'vi.element.event_insurance_requirements_state_change',
  'event_exhibitor_liability_state_change' = 'vi.element.event_exhibitor_liability_state_change',
  'state_change' = 'vi.element.state_change',
  'error' = 'vi.element.error'
}

interface CustomerBase {
  first_name: string;
  last_name: string;
  street?: string;
  city?: string;
  state: StateType | ProvinceType;
  postal_code: string;
  phone_number?: string;
  country?: string;
}

export type Customer = CustomerBase & (
  | {
    email_address: string;
    email_address_hash?: never;
  }
  | {
    email_address?: never;
    email_address_hash: string;
  }
);

export interface ProductDetails {
  name: string;
  friendly_name: string;
  promotional_header: string;
  promotional_description: string;
  descriptors?: string[];
  legal_disclaimer: string;
  perils?: string[];
  carrier_logo_url?: string;
  carrier_name?: string;
  layout?: string;
  accept_action_text?: string;
  decline_action_text?: string;
}

export interface TaxFee {
  amount: number,
  description: string,
  type: string,
  included_in_premium: boolean,
  include_in_marketing_share: boolean,
  per_subscription_charge: boolean
}

export interface QuoteRequest {
  quote_id?: string;
  offer_id?: string;
  customer?: Customer;
  // biome-ignore lint/suspicious/noExplicitAny: dynamic values
  policy_attributes?: Record<string, any>;
  // biome-ignore lint/suspicious/noExplicitAny: dynamic values
  attributes?: Record<string, any>;
  metadata?: Record<string, string>;
  partner_id?: string;
  currency?: string;
  language?: string;
  /** @internal */
  default_selected?: boolean;
  /** @internal */
  default_declined?: boolean;
}

export interface Decision {
  options: {
    status: string;
    error?: {
      code: string;
      message: string;
    },
    data?: QuoteResponse
  }[];
}

export interface ApiResponseV2 {
  offer_id?: string;
  decisions: Decision[];
}

export interface QuoteResponse {
  policy_number?: string;
  policy_status: string;
  quote_id: string;
  offer_id: string;
  vid_id: string;
  policy_holder: Customer;
  // biome-ignore lint/suspicious/noExplicitAny: dynamic values
  policy_attributes: Record<string, any>;
  premium_amount: number;
  total: number;
  taxes_and_fees?: TaxFee[],
  currency: string;
  product: ProductDetails;
  // biome-ignore lint/suspicious/noExplicitAny: dynamic values
  metadata?: Record<string, any>;
  partner?: {
    id?: string;
    legal_business_name?: string;
  };
  subscription_premium?: {
    payment_amount?: number;
    payments_required?: number;
    can_cancel?: boolean;
  };
  recommendation?: {
    message?: string | null;
    enabled: boolean;
  }
}

export type PurchaseResponse = QuoteResponse

export interface ErrorResponse {
  error: string;
  error_code: string;
}

export interface PolicyAttributeSchema {
  type: string;
  properties: Record<string, {
    type: string;
    properties?: Record<string, {
      type: string;
      description: string;
    }>;
  }>;
}

export interface ProductResponse {
  product: ProductDetails;
  policy_attributes_schema: PolicyAttributeSchema;
}

export interface ProductConfig {
  [key: string]: QuoteRequest[];
}

export interface PaymentElementConfig {
  enabled?: boolean;
  button?: boolean;
  customer_secret?: string;

  /* @internal */
  require_customer_updates?: boolean;
}

export interface Config {
  /**
   * Client ID part of your API key.
   */
  client_id: string;

  /**
   * Partner client secret
   * Required for EventInsuranceRequirement and EventExhibitorLiability components.
   */
  partner_client_secret?: string;

  /**
   * Theme options
   */
  theme?: Theme;

  /**
   * Offer ID obtained from VerticalInsure.trackOffer();
   */
  tracking_offer_id?: string;

  /**
   * A unique id for this offer. This value should be defined if there are 
   * multiple offers being presented on a single page. You can typically
   * set this value to an order id, or an item id. 
   */
  unique_offer_id?: string;

  /**
   * Payment configuration
   */
  payments?: PaymentElementConfig;

  /**
   * Customer details. If this customer is provided, it will override any customer
   * defined in any specific product configuration.
   */
  customer?: Customer;

  /**
   * Configuration / quote information for various products
   */
  product_config?: ProductConfig;

  /**
   * Hide offer on specific errors: state_not_available, activity_not_available. Default is true.
   */
  hide_on_error?: boolean;

  /** @internal */
  enable_geolocation?: boolean;

  /**
   * Component type to determine which route to navigate to.
   * Available options are: 
   * - 'event.cancellation'
   * - 'event.insurance_requirements'
   * - 'event.exhibitor_liability'
   * - 'event.room_block_protection'
   */
  component_type?: string;

  /**
   * Additional configuration for the specified component
   */
  component_config?: ComponentConfig;

  /** @internal */
  selection_mode?: 'required' | 'optional';

  /**
   * Map of product key to layout key. Overrides quote.product.layout for layout resolution.
   * For internal testing only.
   * @internal
   */
  layout_override?: Record<string, string>;
}

export interface BaseMessage {
  eventType: EventType
}

export interface ResizeMessage extends BaseMessage {
  eventType: EventType.resize,
  height: number;
  width: number;
}

export interface ReadyMessage extends BaseMessage {
  eventType: EventType.ready
}

export interface InitMessage extends Config, BaseMessage {
  eventType: EventType.init;
  version: string;
  origin: string;
  /** @internal */
  filter_offers_enabled?: boolean;
}

export interface ValidateMessage extends BaseMessage {
  eventType: EventType.validate;
  is_valid?: boolean;
}

export interface TokenizePaymentMessage extends BaseMessage {
  eventType: EventType.tokenize_payment;
  is_valid?: boolean;
  error?: {
    code?: string;
    message?: string;
  },
  token?: string;
}

export interface OfferStateChangeMessage extends BaseMessage {
  eventType: EventType.offer_state_change;
  quotes: QuoteResponse[];
}

export interface PurchaseCompletedMessage extends BaseMessage {
  eventType: EventType.purchase_completed;
  policies: PurchaseResponse[];
}

export interface OfferReadyMessage extends BaseMessage {
  eventType: EventType.offer_state_ready;
  offersAvailable: boolean;
}

export interface FilterOffersMessage extends BaseMessage {
  eventType: EventType.filter_offers;
  requestId: string;
  quoteState: QuoteState;
}

export interface FilterOffersResultMessage extends BaseMessage {
  eventType: EventType.filter_offers_result;
  requestId: string;
  quoteState: QuoteState;
}

export interface EventInsuranceRequirementsStateChangeMessage extends BaseMessage {
  eventType: EventType.event_insurance_requirements_state_change;
  event: {
    event_id: string;
    event_insurance_required?: boolean;
  };
}

export enum EventExhibitorLiabilityStatus {
  'COIValid' = 'COIValid',
  'COIManualReview' = 'COIManualReview',
  'COIManualReviewRejected' = 'COIManualReviewRejected',
  'COIManualReviewApproved' = 'COIManualReviewApproved',
  'OfferAccept' = 'OfferAccept',
  'OfferDecline' = 'OfferDecline',
  'CompleteLater' = 'CompleteLater'
}

export interface EventExhibitorLiabilityStateChangeMessage extends BaseMessage {
  eventType: EventType.event_exhibitor_liability_state_change;
  event_exhibitor: {
    id: string;
    status?: EventExhibitorLiabilityStatus | null;
    quote?: QuoteResponse;
    coi_validation_failures?: { code: string, text: string }[] | null;
    manual_review_request?: string;
  };
}

export type StateChangeMessage = {
  eventType: EventType.state_change
  data: EnabledChangeEvent
  | Omit<OfferStateChangeMessage, 'eventType'>
  | Omit<EventInsuranceRequirementsStateChangeMessage, 'eventType'>
  | Omit<EventExhibitorLiabilityStateChangeMessage, 'eventType'>;
}

export interface EventInsuranceRequirementsStateChangeEvent {
  event_id: string;
  event_insurance_required?: boolean;
}

export interface EventExhibitorLiabilityStateChangeEvent {
  id: string;
  status?: EventExhibitorLiabilityStatus | null;
  quote?: QuoteResponse;
  coi_validation_failures?: { code: string, text: string }[] | null;
  manual_review_request?: string;
}

export interface EnabledChangeEvent {
  enabled: boolean;
  event_id?: string;
}

export interface OfferStateChangeEvent {
  unique_offer_id?: string;
  quotes: QuoteResponse[];
}

export interface PurchaseCompletedEvent {
  unique_offer_id?: string;
  policies: PurchaseResponse[];
}

export interface OfferReadyEvent {
  offersAvailable: boolean;
}

export interface ErrorEvent {
  error: string;
  type: 'validation' | 'server' | 'client';
  source: string;
}

export interface ErrorMessage extends BaseMessage {
  eventType: EventType.error;
  error: string;
  type: 'validation' | 'server' | 'client';
  source: string;
}

export type StateChangeEvent =
  OfferStateChangeEvent
  | EnabledChangeEvent
  | EventInsuranceRequirementsStateChangeEvent
  | EventExhibitorLiabilityStateChangeEvent;

export interface UpdateMessage extends BaseMessage {
  eventType: EventType.update,
  product_config?: ProductConfig;
  component_config?: ComponentConfig;
}

export interface OfferConfig extends Omit<InitMessage, 'eventType'> {
  styles: CSSProperties;
  font_url?: string;
  is_test: boolean;
}

export interface QuoteState {
  [key: string]: (QuoteResponse | ErrorResponse | ApiResponseV2)[];
}

export type OfferContextType = {
  loading: boolean;
  quotes: QuoteState;
  config?: OfferConfig;
  updateProductConfig: (productConfig: ProductConfig) => void;
  shouldDisplayLayout?: boolean;
  hasValidQuote?: boolean;
  acceptedQuotes: string[];
}

export interface CustomerClientSecret {
  customer_id: string;
  customer_email: string;
  secret: string;
}

export type ProductQuotes = (QuoteResponse | ErrorResponse | ApiResponseV2)[];

export interface EventExhibitor {
  id?: string;
  name: string;
  legal_name?: string;
  street: string;
  city: string;
  state: string;
  postal_code: string;
  country: string;
  contact_email_address: string;
  contact_first_name: string;
  contact_last_name: string;
  accepted: boolean;
}

export interface EventConfig {
  id?: string;
  name: string;
  start_date: string;
  end_date: string;
  street: string;
  city: string;
  state: string;
  postal_code: string;
  country: string;
  purpose?: string;
  event_insurance_required?: boolean;
}

export type EventCancellationAddon = "winter_weather" | "windstorm" | "earthquake" | "nonappearance" | "full_terrorism";

export type EventCancellationEvent = {
  event_id?: string;
  event_name?: string;
  event_type?: string;
  event_start_date?: string;
  event_end_date?: string;
  event_country?: string;
  event_state?: string;
  event_city?: string;
  event_venue?: string;
  event_situation?: string;
  gross_revenue?: number;
  costs_and_expenses?: number;
  basis_of_indemnity?: string;
  max_daily_attendees?: number;
};

export interface EventCancellationConfig {
  /**
   * Addon options to provide to the user.
   */
  addons?: EventCancellationAddon[];

  customer: Customer;

  event: EventCancellationEvent;

  metadata?: Record<string, string>;
}

export type RoomBlockProtectionConfig = {
  /**
   * The event id for an event that has already been underwritten
   */
  event_id?: string;
  partner_id?: string;
  event: {
    name: string;
    type: "INDOOR" | "OUTDOOR";
    address: string;
    city: string;
    state: string;
    postal_code: string;
    start_date: string;
    end_date: string;
  }
}

export interface ComponentConfig {

  /**
   * Event configuration for insurance requirements component
   */
  event_config?: EventConfig;

  /**
   * Event exhibitor configuration for exhibitor liability component
   */
  event_exhibitor_config?: EventExhibitor;

  /**
   * Configuration for the event host cancellation component
   */
  event_cancellation?: EventCancellationConfig;

  /**
   * Configuration for room block protection enablement.
   */
  room_block_protection?: RoomBlockProtectionConfig;
}
